// Admin imports hub. Folds the previously-separate /admin/imports
// (JFrog) and /admin/imports/bulk (CSV + tarball) pages into one
// tabbed surface, plus a per-backend split between JFrog and Nexus 3
// imports — each tab pre-selects its source_type, so an operator
// migrating off Nexus lands on a Nexus-only history without having
// to filter past JFrog jobs.
//
// Wire-format paths are unchanged; the per-backend tabs share the
// /api/admin/imports/jfrog endpoint with `?source_type=…` filtering.
// App.tsx redirects /admin/imports/bulk to /admin/imports?tab=bulk so
// existing bookmarks keep working.

import { useSearchParams } from 'react-router-dom';
import { Database, Server, Boxes, FolderGit2, GitFork, Cloud, CloudCog, CloudUpload, type LucideIcon } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { AdminImportsBody } from './AdminImports';
import { AdminBulkImportsBody } from './AdminBulkImports';

type TabKey = 'jfrog' | 'nexus' | 'github' | 'gitlab' | 'azure' | 'awscodeartifact' | 'gcparhybrid' | 'bulk';

interface TabSpec {
  key: TabKey;
  label: string;
  Icon: LucideIcon;
}

const TABS: TabSpec[] = [
  { key: 'jfrog', label: 'JFrog import', Icon: Database },
  { key: 'nexus', label: 'Nexus import', Icon: Server },
  { key: 'github', label: 'GitHub Packages import', Icon: FolderGit2 },
  { key: 'gitlab', label: 'GitLab Packages import', Icon: GitFork },
  { key: 'azure', label: 'Azure Artifacts import', Icon: Cloud },
  { key: 'awscodeartifact', label: 'AWS CodeArtifact import', Icon: CloudCog },
  { key: 'gcparhybrid', label: 'Google Artifact Registry import', Icon: CloudUpload },
  { key: 'bulk', label: 'Bulk import', Icon: Boxes },
];

function isTabKey(v: string | null): v is TabKey {
  return v === 'jfrog' || v === 'nexus' || v === 'github' || v === 'gitlab' ||
    v === 'azure' || v === 'awscodeartifact' || v === 'gcparhybrid' || v === 'bulk';
}

export function AdminImportsHub() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const active: TabKey = isTabKey(raw) ? raw : 'jfrog';

  const setActive = (k: TabKey) => {
    if (k === 'jfrog') {
      setParams({}, { replace: false });
    } else {
      setParams({ tab: k }, { replace: false });
    }
  };

  return (
    <AdminShell
      title="Imports"
      description="Bulk-load artifacts into OrbitalReg. JFrog, Nexus, GitHub Packages, GitLab Package Registry, Azure DevOps Artifacts, AWS CodeArtifact, and Google Artifact Registry imports each drive a remote registry crawl; Bulk imports take a CSV manifest of upstream URLs or a single .tar.gz that becomes one repo's worth of artifacts. All eight share the same job tracker and survive process restarts."
    >
      <div
        role="tablist"
        className="flex flex-wrap items-center gap-2 p-1 rounded-lg bg-bg-inset/40 border border-border w-fit"
      >
        {TABS.map((t) => {
          const isActive = t.key === active;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(t.key)}
              className={
                'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all ' +
                (isActive
                  ? 'bg-nebula/15 text-nebula border border-nebula/40 shadow-sm'
                  : 'text-fg-muted border border-transparent hover:text-fg hover:bg-bg-hover/60')
              }
            >
              <t.Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {active === 'jfrog' && <AdminImportsBody lockSource="jfrog" />}
      {active === 'nexus' && <AdminImportsBody lockSource="nexus" />}
      {active === 'github' && <AdminImportsBody lockSource="github" />}
      {active === 'gitlab' && <AdminImportsBody lockSource="gitlab" />}
      {active === 'azure' && <AdminImportsBody lockSource="azure" />}
      {active === 'awscodeartifact' && <AdminImportsBody lockSource="awscodeartifact" />}
      {active === 'gcparhybrid' && <AdminImportsBody lockSource="gcparhybrid" />}
      {active === 'bulk' && <AdminBulkImportsBody />}
    </AdminShell>
  );
}
